import { RunState, CardId, hasSkill, isSkillMaxed } from "../state/runState";
import { PERKS, PerkId } from "./perks";
import { SKILLS, SkillId } from "./skills";

export type CardDef = {
  id: CardId;
  name: string;
  description: string;
  kind: "skill" | "perk";
  grantsSkill?: SkillId;
};

export const CARDS: Record<CardId, CardDef> = {
  skill_attack: {
    id: "skill_attack",
    name: "Skill: Attack",
    description: SKILLS.attack.description,
    kind: "skill",
    grantsSkill: "attack",
  },
  skill_slash: {
    id: "skill_slash",
    name: "Skill: Slash",
    description: SKILLS.slash.description,
    kind: "skill",
    grantsSkill: "slash",
  },
  skill_defend: {
    id: "skill_defend",
    name: "Skill: Defend",
    description: SKILLS.defend.description,
    kind: "skill",
    grantsSkill: "defend",
  },
  skill_bloodPact: {
    id: "skill_bloodPact",
    name: `Skill: ${SKILLS.bloodPact.name}`,
    description: SKILLS.bloodPact.description,
    kind: "skill",
    grantsSkill: "bloodPact",
  },
  perk_hp: {
    id: "perk_hp",
    name: PERKS.perk_hp.name,
    description: PERKS.perk_hp.description,
    kind: "perk",
  },
  perk_atk: {
    id: "perk_atk",
    name: PERKS.perk_atk.name,
    description: PERKS.perk_atk.description,
    kind: "perk",
  },
  perk_lifesteal: {
    id: "perk_lifesteal",
    name: PERKS.perk_lifesteal.name,
    description: PERKS.perk_lifesteal.description,
    kind: "perk",
  },
  perk_armor: {
    id: "perk_armor",
    name: PERKS.perk_armor.name,
    description: PERKS.perk_armor.description,
    kind: "perk",
  },
  perk_thorns: {
    id: "perk_thorns",
    name: PERKS.perk_thorns.name,
    description: PERKS.perk_thorns.description,
    kind: "perk",
  },
  perk_adrenaline: {
    id: "perk_adrenaline",
    name: PERKS.perk_adrenaline.name,
    description: PERKS.perk_adrenaline.description,
    kind: "perk",
  },
  perk_rage: {
    id: "perk_rage",
    name: PERKS.perk_rage.name,
    description: PERKS.perk_rage.description,
    kind: "perk",
  },
  perk_multi_attack: {
    id: "perk_multi_attack",
    name: PERKS.perk_multi_attack.name,
    description: PERKS.perk_multi_attack.description,
    kind: "perk",
  },
  perk_boss_reflect: {
    id: "perk_boss_reflect",
    name: PERKS.perk_boss_reflect.name,
    description: PERKS.perk_boss_reflect.description,
    kind: "perk",
  },
  perk_boss_heal: {
    id: "perk_boss_heal",
    name: PERKS.perk_boss_heal.name,
    description: PERKS.perk_boss_heal.description,
    kind: "perk",
  },
};

export const getPerkName = (perkId: PerkId): string => CARDS[perkId]?.name ?? perkId;

const CARD_ORDER: CardId[] = [
  "skill_attack",
  "skill_slash",
  "skill_defend",
  "skill_bloodPact",
  "perk_hp",
  "perk_atk",
  "perk_armor",
  "perk_lifesteal",
  "perk_thorns",
  "perk_adrenaline",
  "perk_rage",
];

const pickRandom = <T>(items: T[], excluded: Set<T>): T | undefined => {
  const available = items.filter((item) => !excluded.has(item));
  if (available.length === 0) {
    return undefined;
  }

  const idx = Math.floor(Math.random() * available.length);
  return available[idx];
};

const pickRandomWithRng = <T>(items: T[], excluded: Set<T>, rng: () => number): T | undefined => {
  const available = items.filter((item) => !excluded.has(item));
  if (available.length === 0) {
    return undefined;
  }

  const idx = Math.floor(rng() * available.length);
  return available[idx];
};

export const getRewardChoices = (run: RunState): CardId[] => {
  const choices: CardId[] = [];
  const used = new Set<CardId>();

  // Roll 1/3 chance for skill reward vs perk reward
  const isSkillReward = Math.random() < 1 / 3;

  if (isSkillReward) {
    // Skill reward: return a single skill card (or skill upgrade)
    const skillCards = CARD_ORDER.filter(
      (cardId) => CARDS[cardId].kind === "skill" && !isSkillMaxed(run, CARDS[cardId].grantsSkill!)
    );
    const missingSkillCards = skillCards.filter(
      (cardId) => !hasSkill(run, CARDS[cardId].grantsSkill!)
    );
    const preferredSkillPool = missingSkillCards.length > 0 ? missingSkillCards : skillCards;

    const pick = pickRandom(preferredSkillPool, used);
    if (pick) {
      choices.push(pick);
    }
    // If no skills available, fall back to perks
    if (choices.length === 0) {
      while (choices.length < 3) {
        const perkPick = pickRandom(
          CARD_ORDER.filter((cardId) => CARDS[cardId].kind === "perk"),
          used
        );
        if (!perkPick) {
          break;
        }
        choices.push(perkPick);
        used.add(perkPick);
      }
    }
  } else {
    // Perk reward: return 3 random perk cards
    while (choices.length < 3) {
      const pick = pickRandom(
        CARD_ORDER.filter((cardId) => CARDS[cardId].kind === "perk"),
        used
      );
      if (!pick) {
        break;
      }
      choices.push(pick);
      used.add(pick);
    }
  }

  return choices;
};

/**
 * Deterministic version of getRewardChoices that uses a seeded RNG.
 * This ensures reproducible reward selection for balance testing.
 */
export const getRewardChoicesWithRng = (run: RunState, rng: () => number): CardId[] => {
  const choices: CardId[] = [];
  const used = new Set<CardId>();

  // Roll 1/3 chance for skill reward vs perk reward
  const isSkillReward = rng() < 1 / 3;

  if (isSkillReward) {
    // Skill reward: return a single skill card (or skill upgrade)
    const skillCards = CARD_ORDER.filter(
      (cardId) => CARDS[cardId].kind === "skill" && !isSkillMaxed(run, CARDS[cardId].grantsSkill!)
    );
    const missingSkillCards = skillCards.filter(
      (cardId) => !hasSkill(run, CARDS[cardId].grantsSkill!)
    );
    const preferredSkillPool = missingSkillCards.length > 0 ? missingSkillCards : skillCards;

    const pick = pickRandomWithRng(preferredSkillPool, used, rng);
    if (pick) {
      choices.push(pick);
    }
    // If no skills available, fall back to perks
    if (choices.length === 0) {
      while (choices.length < 3) {
        const perkPick = pickRandomWithRng(
          CARD_ORDER.filter((cardId) => CARDS[cardId].kind === "perk"),
          used,
          rng
        );
        if (!perkPick) {
          break;
        }
        choices.push(perkPick);
        used.add(perkPick);
      }
    }
  } else {
    // Perk reward: return 3 random perk cards
    while (choices.length < 3) {
      const pick = pickRandomWithRng(
        CARD_ORDER.filter((cardId) => CARDS[cardId].kind === "perk"),
        used,
        rng
      );
      if (!pick) {
        break;
      }
      choices.push(pick);
      used.add(pick);
    }
  }

  return choices;
};
